<template>
  <cf-validated-form
    :disabled="isAdding"
    name="addGiftCard"
    @valid-submit="submit"
  >
    <div class="cf-flex cf-flex--column cf-flex--gap-12">
      <div class="cf-flex cf-flex--row cf-flex--align-center cf-flex--gap-16">
        <div class="cf-flex cf-flex--grow-1">
          <cf-text-input
            v-model="cardNumber"
            type="text"
            :label="addGiftCardContent.cardNumberInputPlaceholder"
            name="cardNumber"
            inputmode="numeric"
            mask="giftCard"
            :validations="{
              required: true
            }"
          />
        </div>

        <div
          v-if="giftCardPinRequired"
          class="cf-flex cf-flex--no-shrink cf-flex--flex-basis-md"
        >
          <cf-text-input
            v-model="pin"
            type="text"
            :label="addGiftCardContent.pinInputPlaceholder"
            name="pin"
            allowed-characters="0-9a-zA-Z"
            inputmode="numeric"
            :validations="{
              required: true
            }"
          />
        </div>
      </div>

      <!-- Save Gift Card Checkbox -->
      <div
        v-if="canSaveGiftCardToAccount"
        class="cf-flex cf-flex--column cf-flex--gap-8"
      >
        <cf-checkbox-input
          v-model="saveGiftCard"
          name="shouldSaveGiftCard"
          :disabled="isSaveGiftCardDisabled"
        >
          Save card for future orders
        </cf-checkbox-input>

        <p
          v-if="isSaveGiftCardDisabled"
          class="cf-subtitle cf-subtitle--2"
        >
          You are at your gift card limit to save this card to your account.
        </p>
      </div>

      <button
        class="cf-button cf-button--primary cf-button--lg cf-button--fill"
        :class="{ 'cf-button--loading': isAdding }"
        type="submit"
      >
        {{ addGiftCardContent.cta }}
      </button>
    </div>
  </cf-validated-form>
</template>


<script>
  import { mapGetters } from 'vuex';

  import errorHandlers from '@/services/error-handlers';
  import events from '@/services/events';

  import CfCheckboxInput from '@/view/components/shared/cf-checkbox-input/index.vue';
  import CfValidatedForm from '@/view/components/shared/cf-validated-form/index.vue';
  import CfTextInput from '@/view/components/shared/cf-text-input/index.vue';


  export default {
    name: 'CfAddGiftCard',

    components: {
      CfCheckboxInput,
      CfValidatedForm,
      CfTextInput
    },

    props: {
      paymentGateway: {
        type: Object,
        required: true
      }
    },

    data() {
      return {
        cardNumber: null,
        isAdding: false,
        pin: null,
        saveGiftCardToAccount: true
      };
    },

    computed: {
      ...mapGetters('giftCards', ['canSaveGiftCardToAccount', 'hasReachedAppliedCardLimit', 'hasReachedSavedCardLimit']),
      ...mapGetters('locations', ['selectedLocation']),
      ...mapGetters('content', ['content']),

      addGiftCardContent() {
        const content = this.content.AddGiftCardAtCheckout;

        return {
          cta: content.metadata.applyCardCta,
          cardNumberInputPlaceholder: content.metadata.cardNumberInputPlaceholder,
          pinInputPlaceholder: content.metadata.pinInputPlaceholder
        };
      },

      giftCardPinRequired() {
        return this.paymentGateway.methods?.giftCard?.giftCardPinRequired;
      },

      isSaveGiftCardDisabled() {
        return this.hasReachedSavedCardLimit || this.hasReachedAppliedCardLimit;
      },

      saveGiftCard: {
        get() {
          return this.isSaveGiftCardDisabled ? false : this.saveGiftCardToAccount;
        },

        set(value) {
          this.saveGiftCardToAccount = value;
        }
      }
    },

    methods: {
      handleSubmitError(error) {
        if (error.message === 'card already added') {
          events.emit('notification', {
            type: 'error',
            message: 'Your gift card has already been applied.',
            clickToDismiss: true
          });
        }

        else if (error.message === 'no balance remaining') {
          events.emit('notification', {
            type: 'error',
            message: 'Your gift card has no remaining balance.',
            clickToDismiss: true
          });
        }

        else {
          errorHandlers.generic(error);
        }
      },

      async submit() {
        try {
          this.isAdding = true;

          await this.$store.dispatch('giftCards/addCard', {
            cardNumber: this.cardNumber,
            pin: this.pin,
            saveToAccount: this.saveGiftCard,
            storeId: this.selectedLocation.storeId
          });

          events.emit('notification', {
            type: 'success',
            message: 'Gift card balance applied.'
          });

          // this.$triggerGoogleTrackingEvent('AddNewGiftCard', 'GiftCards');

          this.$triggerNewRelicEvent('AddNewGiftCard', 'Information', 'Added New Gift Card');

          this.$closeModal('addGiftCard');
        }

        catch (error) {
          this.handleSubmitError(error);
        }

        finally {
          this.isAdding = false;
        }
      }
    }
  };
</script>
